import api from '@/services/api';
import type {
    AddAuthorisedContactInput,
    AuthorisedContact,
    AuthorisedContactsPage,
} from '../types/authorisedContacts.types';
import { readString } from '../utils/snapshotReaders';
import { readTimelineMeta } from '../utils/timelineMeta';

export const AUTHORISED_CONTACTS_PER_PAGE = 25;

type RawNode = Record<string, unknown>;

const asString = (value: unknown): string | null => readString(value) ?? null;

const isRecord = (value: unknown): value is RawNode =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** `status` is the server's, with the revocation timestamp as the fallback so a
 *  node missing the key still renders the right group. */
export const mapContactNode = (raw: RawNode): AuthorisedContact => {
    const revokedAt = asString(raw.revoked_at);
    const status = raw.status === 'active' || raw.status === 'revoked'
        ? raw.status
        : revokedAt === null ? 'active' : 'revoked';

    return {
        id: String(raw.id ?? ''),
        name: String(raw.name ?? ''),
        relationship: String(raw.relationship ?? ''),
        status,
        createdAt: String(raw.created_at ?? ''),
        revokedAt,
    };
};

/** Order is the server's; contacts are never re-sorted on the client. */
export const fetchAuthorisedContacts = async (
    agreementId: string,
    params: { page: number; perPage: number },
): Promise<AuthorisedContactsPage> => {
    const raw = (
        await api.get(`/agreements/${agreementId}/authorised-contacts`, {
            params: { page: params.page, per_page: params.perPage },
        })
    ).data;
    const meta = isRecord(raw.meta) ? raw.meta : {};

    return {
        contacts: ((raw.data ?? []) as RawNode[]).map(mapContactNode),
        meta: readTimelineMeta(meta, params),
    };
};

export const addAuthorisedContact = async (
    agreementId: string,
    input: AddAuthorisedContactInput,
): Promise<AuthorisedContact> => {
    return mapContactNode((await api.post(`/agreements/${agreementId}/authorised-contacts`, input)).data.data);
};

/** Reinstates a revoked authorisation. Takes no body, like the revoke. */
export const reauthoriseAuthorisedContact = async (
    agreementId: string,
    contactId: string,
): Promise<AuthorisedContact> =>
    mapContactNode(
        (await api.patch(`/agreements/${agreementId}/authorised-contacts/${contactId}/reauthorise`)).data.data,
    );

/** The revoke takes no body: who revoked and when is the server's, from the JWT. */
export const revokeAuthorisedContact = async (
    agreementId: string,
    contactId: string,
): Promise<AuthorisedContact> =>
    mapContactNode(
        (await api.patch(`/agreements/${agreementId}/authorised-contacts/${contactId}/revoke`)).data.data,
    );
